import {Injectable} from '@angular/core';
import { StateService } from '@uirouter/core';
import { ToastrService } from 'ngx-toastr';
import { ModalService } from '../modals/modal.service';
import { ComplianceItemService } from './compliance-item.service';
import { CplItem } from '../model/cpl-item.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FunctionalityNAModalComponent } from '../supervision-management/compliance-items/compliance-item/modals/function-na-modal.component';
import { ArchiveWarnings } from '../model/archive-warnings.model';
import { CplItemDuplicateData } from '../model/cpl-item-duplicate-data.model';
import { QuestionnaireService } from './questionnaire.service';
import { EdlForm } from '../components/form-builder/models/edl-form.model';
import { QuestionnaireMasterStatus } from '../enums/questionnaire-master-status.enum';
import { DuplicateItemQfmSections } from '../modals/edl-modal-duplicate-item/models/duplicate-item-qfm-sections.model';
import { DuplicateItemQfm } from '../modals/edl-modal-duplicate-item/models/duplicate-item-qfm.model';
import { DuplicateItemForm } from '../modals/edl-modal-duplicate-item/models/duplicate-item-form.model';
import { DuplicateItemActions } from '../enums/duplicate-item-actions.enum';
import { QuestionnaireMasterAvailability } from '../enums/questionnaire-master-availability.enum';
import { COURSE_ACTIVITY_NAME_MAX_LENGTH } from '../constants/a48.constants';
import { SessionService } from '@edl-development/edl-common';
import { firstValueFrom } from 'rxjs';

const MY_SUPERVISOR_ITEMS_STATE = 'authenticated.compliance-items.my-supervision-items';

@Injectable()
export class ComplianceItemActionService {

    constructor(
        private complianceItemService: ComplianceItemService,
        private modalService: ModalService,
        private toastr: ToastrService,
        private stateService: StateService,
        private sessionService: SessionService,
        private ngbModal: NgbModal,
        private questionnaireService: QuestionnaireService
    ) {}

    archive(complianceItem: CplItem) {

        this.complianceItemService
            .getArchiveWarnings(complianceItem.ItemID)
            .subscribe((response: ArchiveWarnings) => {
                this.modalService
                .archvieItem('Confirm', 'Are you sure you want to archive this compliance item?', response)
                .then(() => {
                    return this.complianceItemService
                        .archiveComplianceItem(complianceItem)
                        .subscribe((response) => {
                            this.toastr.success('Compliance Item successfully archived.');
                            this.refreshList();
                        });
                }, () => {});
            });
    }

    activate(complianceItem: CplItem) {
        const needsConfirmation =  this.sessionService.PortalSettings.AutoAssignmentEnabled === true && complianceItem.ItemPackets.length > 0;
       
        const activateCplItem = () => {
            this.complianceItemService
                .activateComplianceItem(complianceItem)
                .subscribe((response) => {
                    this.toastr.success('Compliance Item successfully activated.');
                    this.refreshList();
                });
        }

        if (needsConfirmation) {
            this.modalService
                .confirm('Confirm', 'Activating this compliance item  may cause an auto-assignment to occur. Are you sure you want to continue?')
                .then(() => {
                    activateCplItem();
                })
                .catch(() => {});
        } else {
            activateCplItem();
        }
    }

    convertToTemplate(complianceItem: CplItem) {
        if (complianceItem.QfmID) {
            this.questionnaireService
                .getQuestionnaireById(complianceItem.QfmID)
                .subscribe((questionnaire: EdlForm) => {
                    const questinonaireSections = this.generateQuestionnnaireSections(questionnaire);
                    this.performToTemplateConversion(complianceItem, questinonaireSections);
                });
        } else {
            this.performToTemplateConversion(complianceItem, []);
        }
    }

    private performToTemplateConversion(complianceItem: CplItem, questinonaireSections: DuplicateItemQfmSections[]) {
        this.modalService
            .duplicateItem({
                modalTitle: 'Convert Compliance Item To Template',
                itemTitle: complianceItem.Title,
                maxTitleLength: COURSE_ACTIVITY_NAME_MAX_LENGTH,
                questionnaireSections: questinonaireSections,
                status: QuestionnaireMasterStatus.TEMPLATE,
                availability: QuestionnaireMasterAvailability.COMPLIANCE_ITEMS,
                isTemplate: complianceItem.IsTemplate,
                toTemplate: true,
                cplItemPermissions: complianceItem.Permissions
            })
            .then((result: DuplicateItemForm) => {
                const duplicateData = this.generateCplItemDuplicateData(complianceItem, result);

                this.complianceItemService
                    .convertComplianceItemToTemplate(duplicateData)
                    .subscribe((response: CplItem) => {
                        this.toastr.success('Compliance Item successfully converted to template.');
                        
                        this.stateService.go('authenticated.compliance-items.compliance-item.edit', {
                            complianceItemId: response.ItemID,
                            page: 1
                        },{
                            reload: true
                        });

                    });
            }, () => {});
    }

    delete(complianceItem: CplItem) {
        this.modalService
            .confirm('Confirm', 'Are you sure you want to delete this compliance item? <br/><br/><strong>Please consider archiving instead. </strong> <br/>If deleted, access to this item will be removed from all target user summary pages.  ')
            .then(() => {
                this.complianceItemService
                    .deleteComplianceItem(complianceItem)
                    .subscribe((response) => {
                        this.toastr.success('Compliance Item successfully deleted.');
                        this.goBackToSupervisionManagement();

                    });
            }, () => {});
    }

    duplicate(complianceItem: CplItem) {
        if(!this.sessionService.PortalSettings.ComplyEnabled && complianceItem.IsTemplate){
            this.ngbModal.open(FunctionalityNAModalComponent, {
                size: 'lg',
                backdrop: 'static'
            });
        } else if (complianceItem.QfmID) {
            this.questionnaireService
                .getQuestionnaireById(complianceItem.QfmID)
                .subscribe((questionnaire: EdlForm) => {
                    const questinonaireSections = this.generateQuestionnnaireSections(questionnaire);
                    this.performDuplicate(complianceItem, questinonaireSections);
                });
        } else {
            this.performDuplicate(complianceItem, []);
        }       
    }

    private performDuplicate(complianceItem: CplItem, questinonaireSections: DuplicateItemQfmSections[]){
        this.modalService
            .duplicateItem({
                modalTitle: 'Duplicate Compliance Item',
                itemTitle: complianceItem.Title,
                maxTitleLength: COURSE_ACTIVITY_NAME_MAX_LENGTH,
                questionnaireSections: questinonaireSections,
                status: QuestionnaireMasterStatus.PUBLISHED,
                availability: QuestionnaireMasterAvailability.COMPLIANCE_ITEMS,
                isTemplate: complianceItem.IsTemplate,
                toTemplate: false,
                cplItemPermissions: complianceItem.Permissions
            })
            .then((result: DuplicateItemForm) => {
                const duplicateData = this.generateCplItemDuplicateData(complianceItem, result);

                this.complianceItemService
                    .duplicateComplianceItem(duplicateData)
                    .subscribe((response: CplItem) => {
                        
                        this.stateService.go('authenticated.compliance-items.compliance-item.edit', {
                            complianceItemId: response.ItemID,
                            page: 1
                        },{
                            reload: true
                        });

                    });
            }, () => {});
    }

    restore(complianceItem: CplItem) {
        const needsConfirmation = complianceItem.ItemPackets.length > 0;

        const restoreCplItem = () => {
            this.complianceItemService
                .restoreComplianceItem(complianceItem)
                .subscribe((response) => {
                    this.toastr.success('Compliance Item successfully restored.');
                    window.location.reload();
                });
        }

        if (needsConfirmation) {
            this.modalService
                .confirm('Confirm', 'Restoring this compliance item may cause an auto-assignment to occur. Are you sure you want to continue?')
                .then(() => {
                    restoreCplItem();
                }, () => {});
        } else {
            restoreCplItem();
        }
    }

    remove(complianceItem: CplItem) {
        this.modalService
            .confirm('Confirm', 'Are you sure you want to remove this compliance item?')
            .then(() => {
                this.complianceItemService
                    .removeComplianceItem(complianceItem)
                    .subscribe((response) => {
                        this.toastr.success('Compliance Item successfully removed.');
                        this.goBackToSupervisionManagement();

                    });
            }, () => {});
    }

    requestAccess(complianceItem: CplItem) {
        firstValueFrom(this.complianceItemService.requestAccess(complianceItem.ItemID))
            .then(() => {
                this.toastr.success('Request was sent successfully.');
            });
    }

    private goBackToSupervisionManagement(){
        if(this.stateService.includes(MY_SUPERVISOR_ITEMS_STATE)) {
            this.refreshList();
        } else {
            this.stateService
                .go(MY_SUPERVISOR_ITEMS_STATE + '.all', {
                    page: 1
                }, {reload:true});
        }
    }

    private refreshList(){
        this.sessionService.Loading(true);
        this.stateService
                .go(this.stateService.$current.name, this.stateService.params, {
                    reload: true
                });
    }

    private generateQuestionnnaireSections(questionnaire: EdlForm) {
        const qfm = new DuplicateItemQfm({
            QfmTitle: questionnaire.Title,
            QfmId: questionnaire.FormID.toString(),
            IsTemplate: questionnaire.IsTemplate
        });

        const section = new DuplicateItemQfmSections({
            title: 'Questionnaires',
            questionnaires: [qfm]
        });
    
        return [section];
    }

    private generateCplItemDuplicateData(
        cplItem: CplItem,
        qfmData: DuplicateItemForm
    ) {
        const questionnaire = (qfmData.sections?.length > 0 && qfmData.sections[0].questionnaires?.length > 0) ? qfmData.sections[0].questionnaires[0] : null;

        const duplicateData = new CplItemDuplicateData();
        duplicateData.ItemModel = cplItem;
        duplicateData.ShouldDuplicateRoster = qfmData.shouldDuplicateRoster;
        duplicateData.ShouldDupItemPackets = qfmData.shouldDupItemPackets;
        duplicateData.Title = qfmData.title;
        duplicateData.EdlTemplateTypes = qfmData.EdlTemplateTypes;

        if (questionnaire) {
            switch(questionnaire.action.toString()) {
                case DuplicateItemActions.REMOVE.toString():
                    break;
                case DuplicateItemActions.DUPLICATE.toString():
                    duplicateData.QfmId = cplItem.QfmID;
                    duplicateData.QfmTitle = questionnaire.duplicateQfmTitle;
                    break;
                case DuplicateItemActions.REPLACE.toString():
                    duplicateData.QfmId = questionnaire.newQfmId;
                    break;
                case DuplicateItemActions.KEEP.toString():
                default:
                    duplicateData.QfmId = cplItem.QfmID;
                    break;
            }
        }

        return duplicateData;
    }
}

